import React, { useState } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import {
  Briefcase, MapPin, Clock, Users, Rocket, Heart, GraduationCap, Send, ArrowRight, CheckCircle2, Building2,
} from "lucide-react";
import { useToast } from "../hooks/use-toast";

const JOBS = [
  {
    id: "j1",
    title: "Travel Sales Executive",
    dept: "Sales",
    location: "Taranagar, Churu · Rajasthan",
    type: "Full-time",
    exp: "1-3 years",
    salary: "₹2.4 - 4.8 LPA + Incentives",
    tags: ["Sales", "Customer Success", "On-site"],
    description: "Consult with travellers, design custom itineraries and convert enquiries into confirmed bookings. Handle package, flight, hotel, bus & cab bookings end-to-end.",
  },
  {
    id: "j2",
    title: "Fleet Operations Manager",
    dept: "Operations",
    location: "Taranagar, Churu · Rajasthan",
    type: "Full-time",
    exp: "3-6 years",
    salary: "₹4.8 - 7.2 LPA",
    tags: ["Ops", "Fleet", "Team Lead"],
    description: "Own the daily deployment of our bus & cab fleet — driver scheduling, vehicle maintenance, on-trip customer support, and fuel/toll reconciliation.",
  },
  {
    id: "j3",
    title: "Digital Marketing Executive",
    dept: "Marketing",
    location: "Remote / Jaipur",
    type: "Full-time",
    exp: "1-2 years",
    salary: "₹3 - 5 LPA",
    tags: ["SEO", "Ads", "Content"],
    description: "Grow the PMR Travels brand online through SEO, Meta & Google Ads, Instagram content, and email campaigns. Ownership of traffic + conversion KPIs.",
  },
  {
    id: "j4",
    title: "Tour Manager (Group Trips)",
    dept: "Operations",
    location: "Pan-India travel",
    type: "Full-time",
    exp: "2-4 years",
    salary: "₹3.6 - 5.4 LPA + Travel Perks",
    tags: ["Travel", "Group Leader", "Field"],
    description: "Escort group tours across India — handle logistics, guide interactions, guest experience and daily reports. Free travel across 15+ destinations a year.",
  },
  {
    id: "j5",
    title: "Full-stack Developer (React / FastAPI)",
    dept: "Technology",
    location: "Remote",
    type: "Full-time",
    exp: "2-5 years",
    salary: "₹8 - 14 LPA",
    tags: ["React", "Python", "MongoDB"],
    description: "Build features for pmrtravels.com — booking flows, payment integrations (Paytm/UPI), and internal tools. Modern stack, small team, big ownership.",
  },
  {
    id: "j6",
    title: "Customer Support Executive",
    dept: "Support",
    location: "Taranagar / Remote",
    type: "Full-time · Rotational shifts",
    exp: "0-2 years",
    salary: "₹2.1 - 3.6 LPA",
    tags: ["Support", "Communication", "Multilingual"],
    description: "Be the reassuring voice for travellers before, during, and after their journey. Hindi + English fluency required, Rajasthani / Tamil / Telugu is a bonus.",
  },
];

const PERKS = [
  { icon: Rocket, title: "Fast-growing startup", desc: "Join us at 2025 launch — lots of room to grow into leadership roles." },
  { icon: Heart, title: "Real work-life balance", desc: "5.5-day work week, generous leave policy, and flexible hours where possible." },
  { icon: GraduationCap, title: "Learn & travel", desc: "1 free familiarisation trip per year to any of our tour destinations." },
  { icon: Users, title: "Family culture", desc: "Small close-knit team where every voice matters — no bureaucracy." },
];

const CareersPage = () => {
  const { toast } = useToast();
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", cover: "" });
  const [submitted, setSubmitted] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast({ title: "Missing details", description: "Name, email and phone are required." });
      return;
    }
    setSubmitted(true);
  };

  const close = () => {
    setSelected(null);
    setSubmitted(false);
    setForm({ name: "", email: "", phone: "", cover: "" });
  };

  return (
    <>
      <Header />
      <main>
        <section className="relative pt-40 pb-20 md:pt-48 md:pb-24 overflow-hidden">
          <img
            src="https://images.unsplash.com/photo-1552664730-d307ca884978?w=1920&q=80"
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950/85 to-slate-900/50" />
          <div className="relative max-w-7xl mx-auto px-6 text-white">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur border border-white/20 text-white text-xs font-semibold tracking-wider uppercase">
              <Briefcase className="w-3.5 h-3.5" /> Careers at PMR Travels
            </div>
            <h1 className="mt-4 text-4xl md:text-6xl font-bold tracking-tight max-w-3xl" style={{ fontFamily: "'Poppins', sans-serif" }}>
              Build the future of travel with us
            </h1>
            <p className="mt-4 text-white/85 text-lg max-w-2xl">
              We're a young, ambitious team based in Rajasthan on a mission to make Indian travel safer, simpler and more delightful. Come join.
            </p>
          </div>
        </section>

        {/* Perks */}
        <section className="py-16 md:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-100 text-teal-700 text-xs font-semibold tracking-wider uppercase">
                <span className="w-1.5 h-1.5 rounded-full bg-teal-600" /> Why PMR
              </div>
              <h2 className="mt-4 text-3xl md:text-4xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: "'Poppins', sans-serif" }}>
                Perks & culture
              </h2>
            </div>
            <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {PERKS.map((p) => (
                <div key={p.title} className="p-5 rounded-2xl border border-slate-200 hover:border-teal-400 hover:shadow-lg transition">
                  <div className="w-11 h-11 rounded-lg bg-teal-600 text-white flex items-center justify-center">
                    <p.icon className="w-5 h-5" />
                  </div>
                  <h3 className="mt-3 font-bold text-slate-900">{p.title}</h3>
                  <p className="mt-1 text-sm text-slate-600 leading-relaxed">{p.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Open Roles */}
        <section className="py-16 md:py-20 bg-slate-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
              <div className="max-w-2xl">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-100 text-orange-700 text-xs font-semibold tracking-wider uppercase">
                  <Building2 className="w-3.5 h-3.5" /> {JOBS.length} Open Positions
                </div>
                <h2 className="mt-4 text-3xl md:text-4xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: "'Poppins', sans-serif" }}>
                  Current openings
                </h2>
                <p className="mt-3 text-slate-600">Don't see the right fit? Send your resume anyway — we're always hiring exceptional people.</p>
              </div>
            </div>

            <div className="mt-10 grid gap-4">
              {JOBS.map((j) => (
                <div key={j.id} className="group bg-white rounded-2xl border border-slate-200 hover:border-teal-500 hover:shadow-xl transition p-5 md:p-6 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 items-center">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge className="bg-teal-100 text-teal-800 hover:bg-teal-100">{j.dept}</Badge>
                      <Badge variant="outline">{j.type}</Badge>
                    </div>
                    <h3 className="mt-2 text-lg md:text-xl font-bold text-slate-900">{j.title}</h3>
                    <p className="mt-1 text-sm text-slate-600">{j.description}</p>
                    <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
                      <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {j.location}</span>
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {j.exp}</span>
                      <span className="flex items-center gap-1"><Briefcase className="w-3 h-3" /> {j.salary}</span>
                    </div>
                  </div>
                  <div className="flex md:flex-col items-end gap-2">
                    <Button onClick={() => setSelected(j)} className="bg-orange-500 hover:bg-orange-600 gap-1">
                      Apply <ArrowRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-10 rounded-2xl bg-gradient-to-r from-teal-700 to-teal-600 text-white p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
              <div>
                <h3 className="text-xl md:text-2xl font-bold" style={{ fontFamily: "'Poppins', sans-serif" }}>Didn't find your role?</h3>
                <p className="mt-1 text-white/85 text-sm">Email your resume to <a href="mailto:careers@pmrtravels.com" className="underline font-semibold">careers@pmrtravels.com</a> — we'll get back if there's a fit.</p>
              </div>
              <Button asChild className="bg-white text-teal-700 hover:bg-slate-100 gap-2">
                <a href="mailto:careers@pmrtravels.com"><Send className="w-4 h-4" /> Send Resume</a>
              </Button>
            </div>
          </div>
        </section>
      </main>
      <Footer />

      {/* Apply dialog */}
      <Dialog open={!!selected} onOpenChange={(v) => !v && close()}>
        <DialogContent className="max-w-lg">
          {selected && !submitted && (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl" style={{ fontFamily: "'Poppins', sans-serif" }}>Apply for {selected.title}</DialogTitle>
                <DialogDescription>{selected.dept} · {selected.location} · {selected.type}</DialogDescription>
              </DialogHeader>
              <form onSubmit={submit} className="mt-4 space-y-4">
                <div>
                  <Label htmlFor="aname" className="text-xs">Full Name *</Label>
                  <Input id="aname" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ravi Kumar" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="aemail" className="text-xs">Email *</Label>
                    <Input id="aemail" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" />
                  </div>
                  <div>
                    <Label htmlFor="aphone" className="text-xs">Phone *</Label>
                    <Input id="aphone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+91 98765 43210" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="acover" className="text-xs">Why do you want to join PMR? (optional)</Label>
                  <Textarea id="acover" rows={3} value={form.cover} onChange={(e) => setForm({ ...form, cover: e.target.value })} placeholder="Tell us a bit about your experience and why this role excites you..." />
                </div>
                <div className="rounded-lg bg-teal-50 border border-teal-100 p-3 text-xs text-slate-700">
                  Please email your resume to <a href="mailto:careers@pmrtravels.com" className="font-semibold text-teal-700 underline">careers@pmrtravels.com</a> with the subject <span className="font-mono">Application — {selected.title}</span>. We'll receive both your form and resume together.
                </div>
                <Button type="submit" className="w-full h-11 bg-teal-600 hover:bg-teal-700">
                  Submit Application
                </Button>
              </form>
            </>
          )}
          {submitted && (
            <div className="text-center py-4">
              <div className="w-16 h-16 rounded-full bg-green-100 mx-auto flex items-center justify-center">
                <CheckCircle2 className="w-9 h-9 text-green-600" />
              </div>
              <h3 className="mt-5 text-2xl font-bold text-slate-900" style={{ fontFamily: "'Poppins', sans-serif" }}>Application received!</h3>
              <p className="mt-2 text-slate-600 text-sm">Thanks {form.name.split(" ")[0]}. Our HR team will review your profile and get back to you within 5 business days.</p>
              <div className="mt-4 text-[11px] text-slate-500">Please also email your resume to <span className="font-mono">careers@pmrtravels.com</span></div>
              <Button onClick={close} className="mt-6 bg-teal-600 hover:bg-teal-700">Close</Button>
              <p className="mt-4 text-[11px] text-orange-600 font-semibold">DEMO MODE — Application form is MOCKED (no email is actually sent yet).</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CareersPage;
